'use strict';

import Utils from 'components/utils';
import validationUtils from 'components/input/validation/utils';
import handlebars from 'handlebars';
import './component.scss';

const MAIN_CONTAINER = 'main-container',
      SHOW_LOADING = 'show-loading',
      LOADING_CONTAINER = 'loading-wrapper',
      CANCEL_LOADING_BTN = 'cancelLoadingBtn',
      TIMEOUT_DEFAULT = 3000;

let observer,
    previousHash = 0,
    loadingPlaceholderEl;

function isSafariOrIOS()
{
    return isSafari() || Utils.isIOS();
}

function isSafari()
{
    return /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
}

function isFirefox()
{
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
}

function init()
{
    loadingPlaceholderEl = document.querySelector(`.${LOADING_CONTAINER}`);

    if (document.querySelectorAll('.' + LOADING_CONTAINER + ', .' + MAIN_CONTAINER).length === 2) {
        init0();

        if (!observer) {
            new MutationObserver(mutations => {
                mutations.forEach(mutation => mutation.addedNodes.length && init0());
            }).observe(document.body, { subtree: true, childList: true });

            observer = true;
        }
    }

    // render default template on init
    // FF did't render the loading image after form submition
    renderDefault();
}

function init0()
{
    for (const button of document.querySelectorAll(`input[type="submit"], button[type="submit"], button:not([type]), a[data-dynamic-loading="true"]`)) {
        // Skip some buttons:
        // - having no form (!button.form).
        // - that were already processed (button.dataset.loading !== undefined).
        // - that must be skipped (button.dataset.noLoading !== undefined)
        const isALink = button.tagName.toLowerCase() === 'a';
        if (!isALink) {
            if (!button.form || button.dataset.loading !== undefined || button.dataset.noLoading !== undefined) {
                continue;
            }
        }

        button.dataset.loading = '';
        // when it is normal <a> link you don't need additional processing about Safari/iOS
        if (isSafariOrIOS() && !isALink) {
            let submit;
            button.addEventListener('click', event => {
                if (submit) {
                    submit = false;

                    return;
                }

                event.preventDefault();

                if (validationFor(button)) {
                    let dataset = button.dataset,
                        timeout = dataset.timeout ? parseInt(dataset.timeout) : TIMEOUT_DEFAULT;
                    if (timeout > 300) {
                        timeout = 300;
                    }

                    if (timeout == 0) {
                        button.style.pointerEvents = 'none';
                    }

                    setTimeout(() => {
                        if (!dataset.dynamicLoadingText) {
                            show(0)
                        } else {
                            showDynamic(dataset);
                        }

                        submit = true;
                        button.click();
                    }, timeout);
                } else {
                    submit = true;
                    button.click();
                }
            });
        } else {
            button.addEventListener('click', () => {
                if (validationFor(button)) {
                    let dataset = button.dataset,
                        timeout = dataset.timeout ? parseInt(dataset.timeout) : TIMEOUT_DEFAULT;
                    if (timeout == 0) {
                        button.style.pointerEvents = 'none';
                    }

                    if (!dataset.dynamicLoadingText) {
                        show(timeout)
                    } else {
                        if (isALink) {
                            showDynamic(dataset, timeout);
                        } else {
                            showDynamic(dataset);
                        }
                    }
                }
            });
        }
    }
}

function validationFor(button)
{
    if (button.tagName.toLowerCase() === 'a') {
        // Skip validation if loading is called by <a> link
        return true;
    }
    const inputDateExpiryList = document.querySelectorAll('.input-date-expiry'),
          validInputDateExpiryList = !inputDateExpiryList.length || validationUtils.validateDate(inputDateExpiryList);

    return button.formNoValidate ||
           (validInputDateExpiryList && button.form.checkValidity()) ||
           validationUtils.getInvalidInputsAndForceSubmit(button.form, false).forceSubmit;
}

function show(timeout)
{
    renderDefault();
    handleAccessibility();
    setTimeout(onShow, timeout);
}

function renderDefault()
{
    if (loadingPlaceholderEl) {
        if (!hasCachedTemplate(loadingPlaceholderEl.dataset)) {
            loadingPlaceholderEl.innerHTML = getLoadingTemplate(loadingPlaceholderEl.dataset);

            let cancelBtn = loadingPlaceholderEl.querySelector(`.${CANCEL_LOADING_BTN}`);
            if (cancelBtn) {
                cancelBtn.addEventListener('click', () => {
                    if (isSafariOrIOS()) {
                        try {
                            window.stop();
                        } catch (exception) {
                            document.execCommand('Stop');
                        }
                    }

                    onClose();
                });
            }
        }
    }
}

function onShow()
{
    //Adding this check to selectively control the display of the loader for front-end validations.
    if (!document.querySelector(`.${MAIN_CONTAINER}`).classList.contains('do-not-load')) {
        document.querySelector(`.${MAIN_CONTAINER}`).classList.add(SHOW_LOADING);
    }
}

function onClose()
{
    document.querySelector(`.${MAIN_CONTAINER}`).classList.remove(SHOW_LOADING);
}

function showDynamic(attributes, timeout)
{
    if (attributes && attributes.dynamicLoading) {
        const data = getSubsetOfObject(attributes, [
            "dynamicLoadingIcon",
            "dynamicLoadingText",
            "dynamicLoadingCancel"
        ]);

        if (!hasCachedTemplate(attributes)) {
            loadingPlaceholderEl.innerHTML = getLoadingTemplate(data);

            let cancelBtn = loadingPlaceholderEl.querySelector(`.${CANCEL_LOADING_BTN}`);
            if (cancelBtn) {
                cancelBtn.addEventListener('click', () => {
                    document.dispatchEvent(new CustomEvent('loading-dynamic:close', { cancelable: false, bubbles: true }));

                    onClose();
                });
            }
        }
        handleAccessibility(data.dynamicLoadingText);
        timeout ? setTimeout(onShow, timeout) : onShow();
    }
}

/**
 * Get only specific properties from attributes
 *
 * @param {Object} obj
 * @param {Array} specificProps
 * @return {Object} subset object
 */
function getSubsetOfObject(obj, specificProps)
{
    const subsetObj = {};
    for (const key in obj) {
        if (specificProps.indexOf(key) > -1) {
            subsetObj[key] = obj[key];
        }
    }

    return subsetObj;
}

/**
 * Render template by data passed.
 *
 * @param  {Object} data
 * @return {DomElement}
 */
function getLoadingTemplate(data)
{
    let  template = `<div class="loading-bg"></div>
                    <div class="loading-container">
                      <div class="loading-box">
                        <div class="preloader active-no-hover">
                          {{#if dynamicLoadingIcon}}
                            <svg class="icon icon-image ">
                              <use xlink:href="{{dynamicLoadingIcon}}"></use>
                            </svg>
                          {{/if}}
                        </div>
                        <p>{{#if dynamicLoadingText}}{{dynamicLoadingText}}{{else}}{{defaultMessageText}}{{/if}}</p>
                        {{#if dynamicLoadingCancel}}
                          <div><a class="cancelLoadingBtn">{{dynamicLoadingCancel}}</a></div>
                        {{/if}}
                      </div>
                    </div>`;

    previousHash = getHashFromData(data);

    return handlebars.compile(template)(data);
}

/**
 * Check if current data is cached
 *
 * @param  {Object} data
 * @return {DomElement}
 */
function hasCachedTemplate(data)
{
    let hash = getHashFromData(data);
    let match  = (hash && hash == previousHash) ? true : false;

    return match;
}

/**
 * Get hash from template data object
 *
 * @param  {Object} data
 * @return {Integer}
 */
function getHashFromData(data)
{
    if (data) {
        let dataString = JSON.stringify(data),
            hash = 0,
            i,
            chr;

        if (dataString.length === 0) {
            return hash;
        }

        for (i = 0; i < dataString.length; i++) {
            chr   = dataString.charCodeAt(i);
            hash  = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }

        return hash;
    }

    previousHash = undefined;

    return 0;
}

function handleAccessibility(dynamicText)
{
    let textPlaceholder = document.querySelector('.loading-text-sr'),
        text = loadingPlaceholderEl.dataset.loadingMessageText + (dynamicText ? dynamicText :  loadingPlaceholderEl.dataset.defaultMessageText);

    if (textPlaceholder) {
        textPlaceholder.innerHTML = text;

        setTimeout(function(){
            textPlaceholder.innerHTML = loadingPlaceholderEl.dataset.longLoadingMessageText;
        }, 8000)
    }
}

window.addEventListener('DOMContentLoaded', init);

// Prevent DOM caching on Firefox when you click back button.
if (isFirefox()) {
    window.addEventListener('unload', () => {});
}

export default { init0, show, showDynamic, onClose };